/**
 * Credits Service
 * Handles all credit-related operations including top-ups, withdrawals, and transaction history.
 */
import apiClient from './apiClient';
import { ensureAuthenticated } from './authService';

// Transaction types
export type TransactionType = 'Top Up' | 'Purchase' | 'Sale' | 'Withdrawal';

// Transaction status types
export type TransactionStatus = 'Pending' | 'Completed' | 'Failed' | 'Held';

// Transaction interface
export interface Transaction {
  id: number;
  date: string;
  type: TransactionType;
  amount: number;
  status: TransactionStatus;
  releaseDate?: string; // Date when held funds will be available
  itemName?: string;    // Name of the item bought/sold
  buyerId?: string;     // ID of the buyer (for sales)
  sellerId?: string;    // ID of the seller (for purchases)
}

/**
 * Gets the current available credit balance
 */
export const getCreditBalance = async (): Promise<number> => {
  try {
    const response = await apiClient.get('/credits/balance');
    return response.data.balance;
  } catch (error) {
    console.error('Error fetching credit balance:', error);
    return 0;
  }
};

/**
 * Gets the current pending credit balance
 */
export const getPendingCreditBalance = async (): Promise<number> => {
  try {
    const response = await apiClient.get('/credits/pending-balance');
    return response.data.pendingBalance;
  } catch (error) {
    console.error('Error fetching pending credit balance:', error);
    return 0;
  }
};

/**
 * Gets the total credit balance (available + pending)
 */
export const getTotalCreditBalance = async (): Promise<number> => {
  try {
    const [balance, pendingBalance] = await Promise.all([
      getCreditBalance(),
      getPendingCreditBalance(),
    ]);
    return balance + pendingBalance;
  } catch (error) {
    console.error('Error fetching total credit balance:', error);
    return 0;
  }
};

/**
 * Gets transaction history
 */
export const getTransactionHistory = async (): Promise<Transaction[]> => {
  try {
    const response = await apiClient.get('/credits/transactions');
    return response.data;
  } catch (error) {
    console.error('Error fetching transaction history:', error);
    return [];
  }
};

/**
 * Updates a transaction's status
 */
export const updateTransactionStatus = async (transactionId: number, status: TransactionStatus): Promise<void> => {
  try {
    await apiClient.patch(`/credits/transactions/${transactionId}`, { status });
  } catch (error) {
    console.error('Error updating transaction status:', error);
  }
};

/**
 * Process a credit top-up
 */
export const topUpCredits = async (amount: number): Promise<Transaction> => {
  // Validate amount
  if (amount <= 0) {
    throw new Error('Amount must be greater than zero');
  }

  // Check if user is authenticated
  const user = await ensureAuthenticated();
  if (!user) {
    throw new Error('You must be logged in to top up credits');
  }

  const response = await apiClient.post('/credits/top-up', { amount });
  return response.data;
};

/**
 * Process a credit withdrawal
 */
export const withdrawCredits = async (amount: number): Promise<Transaction> => {
  // Validate amount
  if (amount <= 0) {
    throw new Error('Amount must be greater than zero');
  }

  const response = await apiClient.post('/credits/withdraw', { amount });
  return response.data;
};

/**
 * Use credits for a purchase
 */
export const spendCredits = async (amount: number, itemName?: string, sellerId?: string): Promise<Transaction> => {
  // Validate amount
  if (amount <= 0) {
    throw new Error('Amount must be greater than zero');
  }

  const response = await apiClient.post('/credits/spend', {
    amount,
    itemName,
    sellerId
  });

  return response.data;
};

/**
 * Add funds to seller account (held for 7 days)
 */
export const addSellerHeldFunds = async (amount: number, itemName?: string, buyerId?: string): Promise<Transaction> => {
  if (amount <= 0) {
    throw new Error('Amount must be greater than zero');
  }

  const response = await apiClient.post('/credits/seller-funds', {
    amount,
    itemName,
    buyerId
  });

  return response.data;
};

/**
 * Release held funds to available balance
 */
export const releaseHeldFunds = async (transactionId: number): Promise<void> => {
  try {
    await apiClient.post(`/credits/transactions/${transactionId}/release`);
  } catch (error) {
    console.error('Error releasing held funds:', error);
    throw new Error('Failed to release held funds');
  }
};

/**
 * Get credit usage statistics
 */
export const getCreditStats = async (): Promise<{
  assetsCount: number;
  totalSpent: number;
  totalEarned: number;
  pendingEarnings: number;
}> => {
  try {
    const response = await apiClient.get('/credits/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching credit stats:', error);
    return {
      assetsCount: 0,
      totalSpent: 0,
      totalEarned: 0,
      pendingEarnings: 0
    };
  }
};

/**
 * Add credits to user's account
 */
export const addCredits = async (amount: number): Promise<Transaction> => {
  // Validate amount
  if (amount <= 0) {
    throw new Error('Amount must be greater than zero');
  }

  const response = await apiClient.post('/credits/add', { amount });
  return response.data;
};
